import { PreviewModal } from "components/ChatPreviewModal/ChatPreviewModal";
import { Button } from "components/DesignSystem/Button/Button";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import Radio from "components/DesignSystem/RadioGroup/RadioGroup";
import { TextArea } from "components/DesignSystem/TextArea/TextArea";
import { Label, TextInput } from "components/DesignSystem/TextInput/TextInput";
import { AuthoriseEmail } from "components/InvoiceSettings/AuthoriseEmail";
import { Preview } from "components/PreviewModal";
import { PlusIcon } from "components/icons/PlusIcon";
import { Trash } from "components/icons/Trash";
import { FieldArray, Form, useFormikContext } from "formik";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { useState } from "react";
import { useDropzone } from "react-dropzone";
import { useParams } from "react-router-dom";
import {
  useGetInvoiceSettingsQuery,
  useUpdateInvoiceMutation,
  useGetInvoiceCustomerQuery,
  useUpdateInvoiceCustomerMutation,
} from "store/apis/invoices";
import { BackendError } from "types/utils/error";
import { FileIcon } from "components/design/FileIcon";
import { AttachWform } from "./AttachWform";
import { SendInvoiceForm } from "./SendInvoiceModal";
import classNames from "classnames";
import { LoadingIcon } from "components/icons/LoadingIcon";
import { FilePreviewTag } from "components/FilePreviewTag/FilePreviewTag";
import { DownloadInvoice } from "components/DownloadInvoice/DownloadInvoice";
import { NO_REPLY_EMAIL } from "constants/base";
import { Invoice } from "types/Models/invoice";
import { isValidEmail } from "util/isValidEmail";
import { GridLayout } from "./GridLayout";
import { InvoiceCcField } from "./InvoiceCcField";

const AddAttachment = ({ onDrop }: { onDrop: (files: File[]) => void }) => {
  const { getInputProps, open, getRootProps } = useDropzone({
    onDrop,
  });

  return (
    <Button type="button" size="small" onClick={open} {...getRootProps()}>
      <input hidden {...getInputProps()} />
      <span className="t-flex t-gap-1 t-items-center">
        <PlusIcon />
        <span>Add attachment</span>
      </span>
    </Button>
  );
};

export const SendInvoice = ({
  invoice,
  invoiceIdFromProp,
}: {
  invoice: Invoice;
  invoiceIdFromProp?: string;
}) => {
  const { values, setFieldValue } = useFormikContext<SendInvoiceForm>();
  const group = useCurrentGroupContext();
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const [attachment, setAttachment] = useState<string | null>();
  const [tempAttachment, setTempAttachment] = useState<File | null>(null);
  const { alertToast, info: infoToast } = useToast();
  const entityId = useCurrentEntityId();
  const [updateInvoice] = useUpdateInvoiceMutation();
  const [updateInvoiceCustomer, { isLoading: isUpdatingCustomer }] =
    useUpdateInvoiceCustomerMutation();
  const authoriseEmailModal = useModal();
  const { data: invoiceSetting } = useGetInvoiceSettingsQuery(
    {
      groupId: group.uuid,
      entityId,
    },
    {
      skip: !group.uuid || !entityId,
    }
  );

  const { data: customerData } = useGetInvoiceCustomerQuery(
    {
      groupId: group.uuid,
      entityId: entityId!,
      customerId: invoice?.customer?.customer_id!,
    },
    {
      skip: !group.uuid || !entityId || !invoice?.customer?.customer_id,
    }
  );

  const attachmentDataUrI =
    tempAttachment && URL.createObjectURL(tempAttachment);

  const updateInvoiceWform = async (should_send_w_form: boolean) => {
    try {
      await updateInvoice({
        entityId: entityId!,
        groupId: group?.uuid!,
        invoiceId,
        payload: {
          should_send_w_form,
        },
      }).unwrap();
    } catch (error) {
      alertToast(
        { message: (error as BackendError).data?.error?.message },
        error as Error
      );
    }
  };

  const onCancelAuthoriseEmail = () => {
    authoriseEmailModal.close();
    setFieldValue("from_email_type", "INKLE");
  };

  const updateCustomerRecipientEmails = async (emails: string[]) => {
    if (
      !group?.uuid ||
      !entityId ||
      !invoice?.customer?.customer_id ||
      !invoice?.customer
    ) {
      return;
    }

    try {
      await updateInvoiceCustomer({
        groupId: group.uuid,
        entityId: entityId,
        customerId: invoice.customer.customer_id,
        payload: {
          company_name: invoice.customer.company_name,
          email: invoice.customer.email || "",
          phone: invoice.customer.phone || "",
          is_reminders_enabled: invoice.customer.is_reminders_enabled ?? false,
          currency: invoice.customer.currency,
          recipient_emails: emails || [],
        },
      }).unwrap();
    } catch (error) {
      alertToast(
        { message: (error as BackendError).data?.error?.message },
        error as Error
      );
    }
  };

  const recipientEmailOptions =
    customerData?.recipient_emails?.map((email: string) => ({
      value: email,
      label: email,
    })) || [];

  const selectedRecipientEmailOptions = (values.recipients || []).map(
    (email: string) => ({
      value: email,
      label: email,
    })
  );

  return (
    <Form className="t-m-0 t-flex t-flex-col t-gap-8">
      <GridLayout>
        <Label htmlFor="from_email_type">From</Label>
        <Radio.Root
          name="from_email_type"
          defaultValue={values.from_email_type}
          value={values.from_email_type}
          id="from_email_type"
          onValueChange={(value) => {
            if (value === "GMAIL" && !invoiceSetting?.connected_gmail) {
              authoriseEmailModal.open();
            }
            setFieldValue("from_email_type", value);
          }}
        >
          <Radio.Content className="!t-flex-row t-gap-2 sm:!t-flex-col sm:!t-gap-4">
            <Radio.Item value="INKLE">
              <div className="t-text-text-100 t-text-body-sm">Inkle</div>
              <div className="t-text-body-sm t-text-neutral-40">
                {NO_REPLY_EMAIL}
              </div>
            </Radio.Item>
            <Radio.Item value="GMAIL">
              <div className="t-text-text-100 t-text-body-sm">Gmail</div>
              <div className="t-text-body-sm t-text-neutral-40">
                {invoiceSetting?.connected_gmail || "Not connected"}
              </div>
            </Radio.Item>
          </Radio.Content>
        </Radio.Root>
      </GridLayout>

      <GridLayout>
        <Label htmlFor="recipients">To</Label>
        <Combobox
          name="recipients"
          size="small"
          creatable
          isMulti
          withForm
          placeholder="Add email addresses"
          options={recipientEmailOptions}
          value={selectedRecipientEmailOptions}
          isLoading={isUpdatingCustomer}
          onChange={(selectedOptions) => {
            const emails = selectedOptions
              ? (selectedOptions as any[]).map((opt) => opt.value)
              : [];

            if (emails.length === 0) {
              infoToast({
                message: "At least one recipient email is required",
              });
            }
          }}
          onCreateOption={(inputValue) => {
            const trimmedEmail = inputValue.trim();
            if (!isValidEmail(trimmedEmail)) {
              alertToast({ message: "Please enter a valid email address" });
              return;
            }
            const newEmails = [
              ...(customerData?.recipient_emails || []),
              trimmedEmail,
            ];
            setFieldValue("recipients", newEmails);
            updateCustomerRecipientEmails(newEmails);
          }}
        />
      </GridLayout>

      <InvoiceCcField invoice={invoice} />

      <GridLayout>
        <Label htmlFor="subject">Subject</Label>
        <TextInput name="subject" customSize="small" />
      </GridLayout>

      <TextArea name="body" label="Body" rows={13} />

      <Checkbox
        label="Attach W-Form to this Invoice"
        name="attachWform"
        checked={values.attachWform}
        onChange={(e) => {
          setFieldValue("attachWform", e.target.checked);
          updateInvoiceWform(e.target.checked);
        }}
      />
      {values.attachWform && <AttachWform />}
      <div className="t-flex t-gap-2 t-flex-col">
        <Label>Attachments</Label>

        <DownloadInvoice
          invoice={invoice}
          trigger={(isLoading, onClick) => (
            <button
              type="button"
              className="t-inline-flex t-items-center t-gap-2 t-rounded-md t-border t-border-solid t-border-neutral-10 t-p-1.5 t-justify-between t-bg-surface-transparent t-w-full"
              onClick={onClick}
            >
              <div className="t-flex t-gap-1 t-items-center t-w-full">
                <FileIcon fileType="PDF" />
                <span className="t-text-body-sm">
                  {invoice.invoice_number}.pdf
                </span>
                <div className="t-flex t-items-center t-ml-auto">
                  {isLoading && (
                    <span
                      className={classNames(
                        "t-flex t-origin-center t-animate-spin"
                      )}
                    >
                      <LoadingIcon />
                    </span>
                  )}
                </div>
              </div>
            </button>
          )}
        />

        {invoice.attachments?.map((file) => (
          <FilePreviewTag size="small" file={file} key={file.uuid} />
        ))}

        <FieldArray
          name="attachments"
          render={(arrayHelpers) => {
            return values.attachments.map((attachment, index) => (
              <div
                key={attachment.name}
                className="t-inline-flex t-items-center t-gap-2 t-rounded-md t-border t-border-solid t-border-neutral-10 t-p-1.5 t-justify-between"
                role="button"
                onClick={() => setTempAttachment(attachment)}
              >
                <FileIcon
                  fileType={attachment.name.split(".").pop()?.toUpperCase()}
                  size="20"
                />
                <span className="t-text-body-sm">{attachment.name}</span>
                <div className="t-ml-auto">
                  <Button
                    type="button"
                    customType="ghost_icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      arrayHelpers.remove(index);
                    }}
                    size="small"
                  >
                    <span className="t-text-red-50">
                      <Trash />
                    </span>
                  </Button>
                </div>
              </div>
            ));
          }}
        />

        <div className="t-mt-2">
          <AddAttachment
            onDrop={(files) =>
              setFieldValue("attachments", [...values.attachments, ...files])
            }
          />
        </div>
      </div>
      {attachment && (
        <Preview
          showModal={Boolean(attachment)}
          closeModal={() => setAttachment(null)}
          groupId={group.uuid}
          previewId={attachment}
        />
      )}
      {tempAttachment && attachmentDataUrI && (
        <PreviewModal
          title={tempAttachment.name}
          open={Boolean(tempAttachment)}
          onClose={() => setTempAttachment(null)}
        >
          <iframe
            className="t-w-full t-h-[96%]"
            src={attachmentDataUrI}
            title={tempAttachment.name}
          />
        </PreviewModal>
      )}
      <AuthoriseEmail
        isOpen={authoriseEmailModal.isOpen}
        close={onCancelAuthoriseEmail}
      />
    </Form>
  );
};
